import {legacyStateForShop} from './legacy-data.js';

const API_VERSION = '2026-07';
const STATE_NAMESPACE = 'impact_addons';
const STATE_KEY = 'groups';
const APPEARANCE_KEY = 'appearance';

export const DEFAULT_APPEARANCE = {
  selected_border_color: '#ff3b30',
  card_border_color: '#dedede',
  card_background: '#ffffff',
  text_color: '#1f1f1f',
  muted_text_color: '#6d7175',
  show_brand: false,
  enable_collapse: false,
  collapsed_count: 2,
};

export async function adminQuery(query, variables = {}) {
  const result = typeof shopify.query === 'function'
    ? await shopify.query(query, {variables, version: API_VERSION})
    : await directAdminQuery(query, variables);
  if (result.errors?.length) {
    throw new Error(result.errors.map((error) => error.message).join('; '));
  }
  return /** @type {any} */ (result.data || {});
}

async function directAdminQuery(query, variables) {
  const response = await fetch(`shopify:admin/api/${API_VERSION}/graphql.json`, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify({query, variables}),
  });
  const result = await response.json();
  if (!response.ok) {
    const detail = result?.errors?.map?.((error) => error.message).join('; ');
    throw new Error(detail || `ADMIN_API_${response.status}`);
  }
  return result;
}

export async function loadAdminState() {
  const data = await adminQuery(`
    query LoadImpactAddonsState {
      shop { name myshopifyDomain }
      currentAppInstallation {
        id
        groups: metafield(namespace: "${STATE_NAMESPACE}", key: "${STATE_KEY}") {
          value
          jsonValue
        }
        appearance: metafield(namespace: "${STATE_NAMESPACE}", key: "${APPEARANCE_KEY}") {
          value
          jsonValue
        }
      }
    }
  `);

  const installation = data.currentAppInstallation;
  if (!installation?.id) throw new Error('APP_INSTALLATION_NOT_FOUND');

  let stateValue = readJsonMetafield(installation.groups);
  if (!installation.groups) {
    const legacyState = legacyStateForShop(data.shop?.myshopifyDomain);
    if (legacyState) {
      await saveJsonMetafield(installation.id, STATE_KEY, legacyState);
      stateValue = legacyState;
    }
  }

  return {
    installationId: installation.id,
    shop: data.shop,
    state: normalizeState(stateValue),
    appearance: normalizeAppearance(readJsonMetafield(installation.appearance)),
  };
}

export async function saveGroupDraft(group) {
  const latest = await loadAdminState();
  assertAssignmentAvailability(latest.state.groups, group);
  const groups = [...latest.state.groups];
  const index = groups.findIndex((candidate) => candidate.id === group.id);
  const saved = {...group, updatedAt: new Date().toISOString()};
  if (index === -1) groups.unshift(saved);
  else groups[index] = saved;

  const state = {...latest.state, groups};
  await saveJsonMetafield(latest.installationId, STATE_KEY, state);
  return {group: saved, state};
}

export async function deleteGroupDraft(groupId) {
  const latest = await loadAdminState();
  const group = latest.state.groups.find((candidate) => candidate.id === groupId);
  if (!group) return latest.state;

  const publishedProducts = uniqueStrings(group.publishedProductGids);
  if (publishedProducts.length) await deleteAssignments(publishedProducts);

  const state = {
    ...latest.state,
    groups: latest.state.groups.filter((candidate) => candidate.id !== groupId),
  };
  await saveJsonMetafield(latest.installationId, STATE_KEY, state);
  return state;
}

export async function saveAppearance(appearance) {
  const latest = await loadAdminState();
  const normalized = normalizeAppearance(appearance);
  await saveJsonMetafield(latest.installationId, APPEARANCE_KEY, normalized);
  return normalized;
}

export async function loadProducts(ids) {
  const uniqueIds = uniqueStrings(ids);
  if (!uniqueIds.length) return [];

  const data = await adminQuery(`
    query LoadImpactAddonProducts($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on Product {
          id
          title
          handle
          vendor
          status
          featuredImage { url altText }
          variants(first: 100) {
            nodes { id title price availableForSale }
          }
        }
      }
    }
  `, {ids: uniqueIds});

  return (data.nodes || []).filter(Boolean);
}

export async function publishGroup(group, productMap) {
  const enabledItems = group.items.filter((item) => item.enabled !== false);
  if (!group.name.trim()) throw new Error('GROUP_NAME_REQUIRED');
  if (!group.assignments.length) throw new Error('ASSIGNMENTS_REQUIRED');
  if (!enabledItems.length) throw new Error('ADDON_ITEMS_REQUIRED');
  if (enabledItems.length > 10) throw new Error('ADDON_ITEM_LIMIT');
  const latest = await loadAdminState();
  assertAssignmentAvailability(latest.state.groups, group);

  const itemMetaobjectIds = [];
  for (let index = 0; index < enabledItems.length; index += 1) {
    const item = enabledItems[index];
    if (item.variantMode === 'fixed' && !item.fixedVariantGid) {
      throw new Error('FIXED_VARIANT_REQUIRED');
    }
    const product = productMap.get(item.productGid);
    const values = compactValues({
      internal_name: item.customTitle || product?.title || `Add-on ${index + 1}`,
      product: item.productGid,
      variant_mode: item.variantMode || 'auto',
      fixed_variant: item.fixedVariantGid,
      default_variant: item.defaultVariantGid,
      custom_title: item.customTitle,
      short_description: item.shortDescription,
      default_selected: Boolean(item.defaultSelected),
      quantity_mode: item.quantityMode || 'one',
      enabled: true,
    });
    const metaobjectId = await saveMetaobject('$app:addon_item', stableHandle('item', item.id), values);
    itemMetaobjectIds.push(metaobjectId);
    item.shopifyMetaobjectId = metaobjectId;
  }

  const revision = Number(group.publishedRevision || 0) + 1;
  const groupMetaobjectId = await saveMetaobject(
    '$app:addon_group',
    stableHandle('group', group.id),
    {
      internal_name: group.name.trim(),
      enabled: group.enabled !== false,
      items: itemMetaobjectIds,
      schema_version: 1,
      published_revision: revision,
    },
  );

  const currentProducts = uniqueStrings(group.assignments);
  const previouslyPublished = uniqueStrings(group.publishedProductGids);
  const removedProducts = previouslyPublished.filter((id) => !currentProducts.includes(id));

  await setAssignments(currentProducts, groupMetaobjectId);
  await deleteAssignments(removedProducts);

  const published = {
    ...group,
    shopifyMetaobjectId: groupMetaobjectId,
    publishedProductGids: currentProducts,
    publishedRevision: revision,
    status: 'PUBLISHED',
    lastPublishedAt: new Date().toISOString(),
    items: group.items.map((item) => ({...item})),
  };
  const saved = await saveGroupDraft(published);
  return saved.group;
}

async function saveMetaobject(type, handle, values) {
  const data = await adminQuery(`
    mutation UpsertImpactAddonMetaobject($handle: MetaobjectHandleInput!, $values: JSON!) {
      metaobjectUpsert(handle: $handle, values: $values) {
        metaobject { id }
        userErrors { field message code }
      }
    }
  `, {handle: {type, handle}, values});
  assertUserErrors(data.metaobjectUpsert);
  if (!data.metaobjectUpsert?.metaobject?.id) throw new Error('METAOBJECT_UPSERT_FAILED');
  return data.metaobjectUpsert.metaobject.id;
}

async function setAssignments(ownerIds, groupMetaobjectId) {
  for (const batch of chunks(ownerIds, 25)) {
    if (!batch.length) continue;
    const data = await adminQuery(`
      mutation AssignImpactAddonGroup($metafields: [MetafieldsSetInput!]!) {
        metafieldsSet(metafields: $metafields) {
          userErrors { field message code }
        }
      }
    `, {
      metafields: batch.map((ownerId) => ({
        ownerId,
        namespace: '$app',
        key: 'addon_group',
        type: 'metaobject_reference',
        value: groupMetaobjectId,
      })),
    });
    assertUserErrors(data.metafieldsSet);
  }
}

async function deleteAssignments(ownerIds) {
  for (const batch of chunks(ownerIds, 25)) {
    if (!batch.length) continue;
    const data = await adminQuery(`
      mutation RemoveImpactAddonAssignments($metafields: [MetafieldIdentifierInput!]!) {
        metafieldsDelete(metafields: $metafields) {
          userErrors { field message code }
        }
      }
    `, {
      metafields: batch.map((ownerId) => ({ownerId, namespace: '$app', key: 'addon_group'})),
    });
    assertUserErrors(data.metafieldsDelete);
  }
}

async function saveJsonMetafield(ownerId, key, value) {
  const data = await adminQuery(`
    mutation SaveImpactAddonState($metafields: [MetafieldsSetInput!]!) {
      metafieldsSet(metafields: $metafields) {
        userErrors { field message code }
      }
    }
  `, {
    metafields: [{
      ownerId,
      namespace: STATE_NAMESPACE,
      key,
      type: 'json',
      value: JSON.stringify(value),
    }],
  });
  assertUserErrors(data.metafieldsSet);
}

function readJsonMetafield(metafield) {
  if (!metafield) return null;
  if (metafield.jsonValue && typeof metafield.jsonValue === 'object') return metafield.jsonValue;
  try {
    return JSON.parse(metafield.value || 'null');
  } catch {
    return null;
  }
}

function normalizeState(value) {
  const groups = Array.isArray(value?.groups) ? value.groups.map(normalizeGroup) : [];
  return {schemaVersion: 1, groups};
}

function normalizeGroup(group) {
  return {
    id: String(group?.id || newId('group')),
    name: String(group?.name || ''),
    enabled: group?.enabled !== false,
    status: group?.status === 'PUBLISHED' ? 'PUBLISHED' : 'DRAFT',
    assignments: uniqueStrings(group?.assignments),
    publishedProductGids: uniqueStrings(group?.publishedProductGids),
    publishedRevision: Number(group?.publishedRevision || 0),
    shopifyMetaobjectId: group?.shopifyMetaobjectId || null,
    lastPublishedAt: group?.lastPublishedAt || null,
    updatedAt: group?.updatedAt || null,
    items: Array.isArray(group?.items) ? group.items.map(normalizeItem) : [],
  };
}

function normalizeItem(item) {
  return {
    id: String(item?.id || newId('item')),
    productGid: String(item?.productGid || ''),
    variantMode: ['auto', 'fixed', 'selector'].includes(item?.variantMode) ? item.variantMode : 'auto',
    fixedVariantGid: item?.fixedVariantGid || '',
    defaultVariantGid: item?.defaultVariantGid || '',
    customTitle: String(item?.customTitle || ''),
    shortDescription: String(item?.shortDescription || ''),
    defaultSelected: Boolean(item?.defaultSelected),
    quantityMode: item?.quantityMode === 'customer' ? 'customer' : 'one',
    enabled: item?.enabled !== false,
    shopifyMetaobjectId: item?.shopifyMetaobjectId || null,
  };
}

function normalizeAppearance(value) {
  return {
    selected_border_color: color(value?.selected_border_color, DEFAULT_APPEARANCE.selected_border_color),
    card_border_color: color(value?.card_border_color, DEFAULT_APPEARANCE.card_border_color),
    card_background: color(value?.card_background, DEFAULT_APPEARANCE.card_background),
    text_color: color(value?.text_color, DEFAULT_APPEARANCE.text_color),
    muted_text_color: color(value?.muted_text_color, DEFAULT_APPEARANCE.muted_text_color),
    show_brand: Boolean(value?.show_brand),
    enable_collapse: Boolean(value?.enable_collapse),
    collapsed_count: Math.max(1, Math.min(10, Number(value?.collapsed_count || 2))),
  };
}

function color(value, fallback) {
  return /^#[0-9a-f]{6}$/i.test(String(value || '')) ? String(value) : fallback;
}

function compactValues(values) {
  return Object.fromEntries(Object.entries(values).filter(([, value]) => value !== null && value !== undefined && value !== ''));
}

function stableHandle(kind, id) {
  return `impact-addon-${kind}-${String(id).toLowerCase().replace(/[^a-z0-9-]+/g, '-')}`.slice(0, 255);
}

function assertUserErrors(payload) {
  const errors = payload?.userErrors || [];
  if (errors.length) throw new Error(errors.map((error) => error.message).join('; '));
}

function chunks(values, size) {
  const result = [];
  for (let index = 0; index < values.length; index += size) result.push(values.slice(index, index + size));
  return result;
}

function uniqueStrings(value) {
  return [...new Set((Array.isArray(value) ? value : []).filter((item) => typeof item === 'string' && item))];
}

function assertAssignmentAvailability(groups, group) {
  const selected = new Set(uniqueStrings(group.assignments));
  const conflict = groups.find((candidate) => candidate.id !== group.id && candidate.assignments.some((productId) => selected.has(productId)));
  if (conflict) throw new Error(`ASSIGNMENT_CONFLICT: ${conflict.name}`);
}

export function newId(prefix) {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
}

export function createEmptyGroup() {
  return normalizeGroup({id: newId('group'), name: '', enabled: true, status: 'DRAFT'});
}

export function createItem(productGid) {
  return normalizeItem({id: newId('item'), productGid, enabled: true});
}
